import React, { useState, useEffect } from 'react';
import { Users, Layers, ShieldHalf, Building2, Download } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/Tabs';
import dashboardService from '../services/dashboard';

const Reports = () => {
    const [loading, setLoading] = useState(true);
    const [reports, setReports] = useState({
        userActivity: [],
        appUsage: [],
        roleAssignment: [],
        tenantDistribution: [],
    });
    useEffect(() => {
        loadReports();
    }, []);

    const loadReports = async () => {
        setLoading(true);
        try {
            const data = await dashboardService.getReports('all', 100);
            if (data) {
                setReports({
                    userActivity: data.userActivity || [],
                    appUsage: data.appUsage || [],
                    roleAssignment: data.roleAssignment || [],
                    tenantDistribution: data.tenantDistribution || [],
                });
            }
        } catch (err) {
            console.error('Error loading reports:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return dateStr;
        }
    };

    const formatDateOnly = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            });
        } catch {
            return dateStr;
        }
    };

    const exportToCSV = (data, filename) => {
        if (!data || data.length === 0) return;

        const headers = Object.keys(data[0]);
        const csvContent = [
            headers.join(','),
            ...data.map((row) => headers.map((header) => JSON.stringify(row[header] || '')).join(',')),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <Card>
                    <CardContent className="p-6">
                        <div className="h-64 bg-slate-200 rounded animate-pulse" />
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <Tabs defaultValue="user-activity" className="space-y-6">
                <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="user-activity">
                        <Users className="h-4 w-4 mr-2" />
                        User Activity
                    </TabsTrigger>
                    <TabsTrigger value="app-usage">
                        <Layers className="h-4 w-4 mr-2" />
                        App Usage
                    </TabsTrigger>
                    <TabsTrigger value="role-assignment">
                        <ShieldHalf className="h-4 w-4 mr-2" />
                        Role Assignment
                    </TabsTrigger>
                    <TabsTrigger value="tenant-distribution">
                        <Building2 className="h-4 w-4 mr-2" />
                        Tenant Distribution
                    </TabsTrigger>
                </TabsList>

                {/* User Activity Report */}
                <TabsContent value="user-activity" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <Users className="h-5 w-5" />
                                        User Activity Report
                                    </CardTitle>
                                    <CardDescription>Detailed user activity and engagement metrics</CardDescription>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => exportToCSV(reports.userActivity, 'user-activity-report.csv')}
                                >
                                    <Download className="h-4 w-4 mr-2" />
                                    Export CSV
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {reports.userActivity.length === 0 ? (
                                <p className="text-sm text-slate-500 text-center py-8">
                                    No user activity data available
                                </p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b">
                                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">
                                                    User
                                                </th>
                                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">
                                                    Email
                                                </th>
                                                <th className="text-right py-3 px-4 text-sm font-medium text-slate-700">
                                                    Apps
                                                </th>
                                                <th className="text-right py-3 px-4 text-sm font-medium text-slate-700">
                                                    Roles
                                                </th>
                                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">
                                                    Last Activity
                                                </th>
                                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">
                                                    Status
                                                </th>
                                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">
                                                    Created
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reports.userActivity.map((user, index) => (
                                                <tr
                                                    key={index}
                                                    className="border-b hover:bg-slate-50 transition-colors"
                                                >
                                                    <td className="py-3 px-4">
                                                        <div className="font-medium text-slate-900">
                                                            {user.first_name} {user.last_name}
                                                        </div>
                                                        <div className="text-xs text-slate-500">{user.username}</div>
                                                    </td>
                                                    <td className="py-3 px-4 text-slate-600">{user.email_id}</td>
                                                    <td className="py-3 px-4 text-right">
                                                        <Badge variant="secondary">{user.app_count || 0}</Badge>
                                                    </td>
                                                    <td className="py-3 px-4 text-right">
                                                        <Badge variant="outline">{user.role_count || 0}</Badge>
                                                    </td>
                                                    <td className="py-3 px-4 text-slate-600 text-sm">
                                                        {formatDate(user.last_activity)}
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <Badge
                                                            variant={
                                                                user.is_active === 1 || user.is_active === '1'
                                                                    ? 'default'
                                                                    : 'secondary'
                                                            }
                                                        >
                                                            {user.is_active === 1 || user.is_active === '1'
                                                                ? 'Active'
                                                                : 'Inactive'}
                                                        </Badge>
                                                    </td>
                                                    <td className="py-3 px-4 text-slate-600 text-sm">
                                                        {formatDateOnly(user.created_on)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* App Usage Report */}
                <TabsContent value="app-usage" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <Layers className="h-5 w-5" />
                                        Application Usage Report
                                    </CardTitle>
                                    <CardDescription>Comprehensive application usage and statistics</CardDescription>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => exportToCSV(reports.appUsage, 'app-usage-report.csv')}
                                >
                                    <Download className="h-4 w-4 mr-2" />
                                    Export CSV
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {reports.appUsage.length === 0 ? (
                                <p className="text-sm text-slate-500 text-center py-8">No app usage data available</p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b">
                                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">
                                                    Application
                                                </th>
                                                <th className="text-right py-3 px-4 text-sm font-medium text-slate-700">
                                                    Users
                                                </th>
                                                <th className="text-right py-3 px-4 text-sm font-medium text-slate-700">
                                                    Roles
                                                </th>
                                                <th className="text-right py-3 px-4 text-sm font-medium text-slate-700">
                                                    Tenants
                                                </th>
                                                <th className="text-right py-3 px-4 text-sm font-medium text-slate-700">
                                                    Components
                                                </th>
                                                <th className="text-right py-3 px-4 text-sm font-medium text-slate-700">
                                                    New Users (30d)
                                                </th>
                                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">
                                                    Status
                                                </th>
                                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">
                                                    Created
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reports.appUsage.map((app, index) => (
                                                <tr
                                                    key={index}
                                                    className="border-b hover:bg-slate-50 transition-colors"
                                                >
                                                    <td className="py-3 px-4">
                                                        <div className="font-medium text-slate-900">{app.app_name}</div>
                                                    </td>
                                                    <td className="py-3 px-4 text-right">
                                                        <Badge variant="default">{app.total_users || 0}</Badge>
                                                    </td>
                                                    <td className="py-3 px-4 text-right">
                                                        <Badge variant="secondary">{app.total_roles || 0}</Badge>
                                                    </td>
                                                    <td className="py-3 px-4 text-right">
                                                        <Badge variant="outline">{app.total_tenants || 0}</Badge>
                                                    </td>
                                                    <td className="py-3 px-4 text-right">
                                                        <Badge variant="outline">{app.total_components || 0}</Badge>
                                                    </td>
                                                    <td className="py-3 px-4 text-right">
                                                        <Badge variant="secondary">{app.new_users_30d || 0}</Badge>
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <Badge
                                                            variant={
                                                                app.is_active === 1 || app.is_active === '1'
                                                                    ? 'default'
                                                                    : 'secondary'
                                                            }
                                                        >
                                                            {app.is_active === 1 || app.is_active === '1'
                                                                ? 'Active'
                                                                : 'Inactive'}
                                                        </Badge>
                                                    </td>
                                                    <td className="py-3 px-4 text-slate-600 text-sm">
                                                        {formatDateOnly(app.created_on)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Role Assignment Report */}
                <TabsContent value="role-assignment" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <ShieldHalf className="h-5 w-5" />
                                        Role Assignment Report
                                    </CardTitle>
                                    <CardDescription>
                                        Role assignments and distribution across applications
                                    </CardDescription>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => exportToCSV(reports.roleAssignment, 'role-assignment-report.csv')}
                                >
                                    <Download className="h-4 w-4 mr-2" />
                                    Export CSV
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {reports.roleAssignment.length === 0 ? (
                                <p className="text-sm text-slate-500 text-center py-8">
                                    No role assignment data available
                                </p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b">
                                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">
                                                    Role
                                                </th>
                                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">
                                                    Application
                                                </th>
                                                <th className="text-right py-3 px-4 text-sm font-medium text-slate-700">
                                                    Assigned Users
                                                </th>
                                                <th className="text-right py-3 px-4 text-sm font-medium text-slate-700">
                                                    New (30d)
                                                </th>
                                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">
                                                    Status
                                                </th>
                                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">
                                                    Created
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reports.roleAssignment.map((role, index) => (
                                                <tr
                                                    key={index}
                                                    className="border-b hover:bg-slate-50 transition-colors"
                                                >
                                                    <td className="py-3 px-4">
                                                        <div className="font-medium text-slate-900">
                                                            {role.role_name}
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-4 text-slate-600">{role.app_name}</td>
                                                    <td className="py-3 px-4 text-right">
                                                        <Badge variant="default">{role.assigned_users || 0}</Badge>
                                                    </td>
                                                    <td className="py-3 px-4 text-right">
                                                        <Badge variant="secondary">
                                                            {role.new_assignments_30d || 0}
                                                        </Badge>
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <Badge
                                                            variant={
                                                                role.is_active === 1 || role.is_active === '1'
                                                                    ? 'default'
                                                                    : 'secondary'
                                                            }
                                                        >
                                                            {role.is_active === 1 || role.is_active === '1'
                                                                ? 'Active'
                                                                : 'Inactive'}
                                                        </Badge>
                                                    </td>
                                                    <td className="py-3 px-4 text-slate-600 text-sm">
                                                        {formatDateOnly(role.created_on)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Tenant Distribution Report */}
                <TabsContent value="tenant-distribution" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <Building2 className="h-5 w-5" />
                                        Tenant Distribution Report
                                    </CardTitle>
                                    <CardDescription>Tenant distribution and user assignments</CardDescription>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        exportToCSV(reports.tenantDistribution, 'tenant-distribution-report.csv')
                                    }
                                >
                                    <Download className="h-4 w-4 mr-2" />
                                    Export CSV
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {reports.tenantDistribution.length === 0 ? (
                                <p className="text-sm text-slate-500 text-center py-8">
                                    No tenant distribution data available
                                </p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b">
                                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">
                                                    Tenant
                                                </th>
                                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">
                                                    Application
                                                </th>
                                                <th className="text-right py-3 px-4 text-sm font-medium text-slate-700">
                                                    Users
                                                </th>
                                                <th className="text-right py-3 px-4 text-sm font-medium text-slate-700">
                                                    Roles
                                                </th>
                                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">
                                                    Status
                                                </th>
                                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">
                                                    Created
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reports.tenantDistribution.map((tenant, index) => (
                                                <tr
                                                    key={index}
                                                    className="border-b hover:bg-slate-50 transition-colors"
                                                >
                                                    <td className="py-3 px-4">
                                                        <div className="font-medium text-slate-900">
                                                            {tenant.tenant_name}
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-4 text-slate-600">{tenant.app_name}</td>
                                                    <td className="py-3 px-4 text-right">
                                                        <Badge variant="default">{tenant.user_count || 0}</Badge>
                                                    </td>
                                                    <td className="py-3 px-4 text-right">
                                                        <Badge variant="secondary">{tenant.role_count || 0}</Badge>
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <Badge
                                                            variant={
                                                                tenant.is_active === 1 || tenant.is_active === '1'
                                                                    ? 'default'
                                                                    : 'secondary'
                                                            }
                                                        >
                                                            {tenant.is_active === 1 || tenant.is_active === '1'
                                                                ? 'Active'
                                                                : 'Inactive'}
                                                        </Badge>
                                                    </td>
                                                    <td className="py-3 px-4 text-slate-600 text-sm">
                                                        {formatDateOnly(tenant.created_on)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default Reports;
